import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";

import { faMailBulk } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
	faTwitter,
	faGithub,
	faLinkedinIn,
	faInstagram,
} from "@fortawesome/free-brands-svg-icons";

import Logo from "../components/common/logo";
import Footer from "../components/common/footer";
import NavBar from "../components/common/navBar";
import StatusIndicator from "../components/common/statusIndicator";
import Article from "../components/homepage/article";
import ProofStrip from "../components/homepage/proofStrip";
import Testimonials from "../components/homepage/testimonials";
// import TechStack from "../components/homepage/techStack";
import AllProjects from "../components/projects/allProjects";

import INFO from "../data/user";
import SEO from "../data/seo";
import myArticles from "../data/articles";

import "./styles/homepage.css";

const Homepage = () => {
	useEffect(() => {
		window.scrollTo(0, 0);
	}, []);

	// Highlight the section currently in the middle of the viewport as the
	// reader scrolls, so the sections guide the eye without explicit labels.
	useEffect(() => {
		const sections = document.querySelectorAll(".homepage-section");
		if (!("IntersectionObserver" in window) || !sections.length) return;
		const io = new IntersectionObserver(
			(entries) => {
				entries.forEach((e) =>
					e.target.classList.toggle("in-view", e.isIntersecting)
				);
			},
			{ rootMargin: "-45% 0px -45% 0px" }
		);
		sections.forEach((s) => io.observe(s));
		return () => io.disconnect();
	}, []);

	const currentSEO = SEO.find((item) => item.page === "home");

	return (
		<React.Fragment>
			<Helmet>
				<title>{INFO.main.title}</title>
				<meta name="description" content={currentSEO.description} />
				<meta
					name="keywords"
					content={currentSEO.keywords.join(", ")}
				/>
			</Helmet>

			<div className="page-content">
									<NavBar active="home" />
				<div className="content-wrapper">
					<div className="homepage-logo-container">
						<Logo width={80} link={false} />
					</div>

					<div className="homepage-container">
						<div className="homepage-first-area">
							<div className="homepage-first-area-left-side">
								<div className="title homepage-title animate-fade-left">
									{INFO.homepage.title
										.split("\n")
										.map((line, idx, arr) => (
											<React.Fragment key={idx}>
												{line}
												{idx < arr.length - 1 && <br />}
											</React.Fragment>
										))}
								</div>

								<div className="subtitle homepage-subtitle animate-fade-left animate-delay-200">
									<div>
										{INFO.homepage.description
											.split("\n")
											.map((line, idx) => (
												<React.Fragment key={idx}>
													{line}
													<br />
												</React.Fragment>
											))}
									</div>
									<StatusIndicator
										status="available"
										message="Available for engineering leadership roles & select consulting"
									/>
								</div>

								<ProofStrip />
							</div>

							<div className="homepage-first-area-right-side">
								<div className="homepage-image-container animate-fade-right animate-delay-400">
									<div className="homepage-image-wrapper">
										<img
											src="homepage.jpg"
											alt="about"
											className="homepage-image"
										/>
									</div>
								</div>
							</div>
						</div>

						<div className="homepage-socials">

							<a
								href={INFO.socials.linkedin}
								target="_blank"
								rel="noreferrer"
							>
								<FontAwesomeIcon
									icon={faLinkedinIn}
									className="homepage-social-icon"
								/>
							</a>

							<a
								href={INFO.socials.github}
								target="_blank"
								rel="noreferrer"
							>
								<FontAwesomeIcon
									icon={faGithub}
									className="homepage-social-icon"
								/>
							</a>
							<a
								href={INFO.socials.instagram}
								target="_blank"
								rel="noreferrer"
							>
								<FontAwesomeIcon
									icon={faInstagram}
									className="homepage-social-icon"
								/>
							</a>
							<a
								href={INFO.socials.twitter}
								target="_blank"
								rel="noreferrer"
							>
								<FontAwesomeIcon
									icon={faTwitter}
									className="homepage-social-icon"
								/>
							</a>


							<a
								href={`mailto:${INFO.main.email}`}
								target="_blank"
								rel="noreferrer"
							>
								<FontAwesomeIcon
									icon={faMailBulk}
									className="homepage-social-icon"
								/>
							</a>
						</div>

						<Testimonials />

						<div className="homepage-section">
							<div className="homepage-section-head">
								<div className="homepage-section-title">
									{INFO.articles.title}
								</div>
								<Link
									to="/articles"
									className="homepage-section-all"
								>
									All articles →
								</Link>
							</div>
							<div className="homepage-articles">
								{myArticles.slice(0, 2).map((article) => (
									<div
										className="homepage-article"
										key={article.slug}
									>
										<Article
											key={article.slug}
											date={article.date}
											title={article.title}
											description={article.tldr || article.description}
											readingTime={article.readingTime}
											link={"/article/" + article.slug}
										/>
									</div>
								))}
							</div>
						</div>

						<div className="homepage-section">
							<div className="homepage-section-head">
								<div className="homepage-section-title">
									Projects
								</div>
								<Link
									to="/projects"
									className="homepage-section-all"
								>
									All projects →
								</Link>
							</div>
							<div className="homepage-projects">
								<AllProjects limit={2} />
							</div>
						</div>

						<div className="page-footer">
							<Footer />
						</div>
					</div>
				</div>
			</div>
		</React.Fragment>
	);
};

export default Homepage;
